import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SorterResult } from 'antd/es/table/interface';
import { AdminClinicsListTemplate, LoadingTemplate } from '@/components/templates';
import { DistanceCachesFilterPanel, DistanceCachesTableSection } from '@/components/organisms';
import { useAdminDistanceCachesList } from '@/hooks/admin/useAdminDistanceCaches';
import { CSV_SUCCESS_MESSAGE, CSV_ERROR_MESSAGE } from '@/constants';
import type { DistanceCacheListParams, DistanceCacheListItem } from '@/services/admin/distance-caches';
import { downloadDistanceCachesCsv } from '@/services/admin/distance-caches';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';

const DEFAULT_PER_PAGE = 20;

type FilterState = {
  siteTypes: string[];
  clinicNumber: string;
  address1: string;
  address2: string;
};

const getFiltersFromSearchParams = (searchParams: URLSearchParams): FilterState => {
  const siteTypesFromArray = searchParams.getAll('site_types').filter(Boolean);
  const siteTypesFromCsv = (searchParams.get('site_types') ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
  const siteTypes = siteTypesFromArray.length > 0 ? siteTypesFromArray : siteTypesFromCsv;

  return {
    siteTypes,
    clinicNumber: searchParams.get('clinic_number') ?? '',
    address1: searchParams.get('address1') ?? '',
    address2: searchParams.get('address2') ?? '',
  };
};

const getPageFromSearchParams = (searchParams: URLSearchParams): number => {
  const raw = Number(searchParams.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const SiteAdminDistanceCachesListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : (site ?? '')],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : (site ?? undefined)),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] = siteTypesQuery.data?.data?.site_types ?? [];
  const showSiteFilter = tenantSiteTypeList.length > 1;

  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach((t) => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  const tenantSiteTypes = useMemo(
    () => tenantSiteTypeList.map((t) => t.site_type).filter(Boolean) as string[],
    [tenantSiteTypeList]
  );

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  const initialFilters = getFiltersFromSearchParams(searchParams);

  const [siteTypes, setSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [clinicNumber, setClinicNumber] = useState(initialFilters.clinicNumber);
  const [address1, setAddress1] = useState(initialFilters.address1);
  const [address2, setAddress2] = useState(initialFilters.address2);

  const [appliedSiteTypes, setAppliedSiteTypes] = useState<string[]>(initialFilters.siteTypes);
  const [appliedClinicNumber, setAppliedClinicNumber] = useState(initialFilters.clinicNumber);
  const [appliedAddress1, setAppliedAddress1] = useState(initialFilters.address1);
  const [appliedAddress2, setAppliedAddress2] = useState(initialFilters.address2);

  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every((st) => siteTypes.includes(st));

  const [page, setPage] = useState(getPageFromSearchParams(searchParams));
  const sortBy = searchParams.get('sort_by') ?? undefined;
  const order = searchParams.get('order') as 'ascend' | 'descend' | null;

  useEffect(() => {
    const nextFilters = getFiltersFromSearchParams(searchParams);
    setSiteTypes(nextFilters.siteTypes);
    setClinicNumber(nextFilters.clinicNumber);
    setAddress1(nextFilters.address1);
    setAddress2(nextFilters.address2);
    setAppliedSiteTypes(nextFilters.siteTypes);
    setAppliedClinicNumber(nextFilters.clinicNumber);
    setAppliedAddress1(nextFilters.address1);
    setAppliedAddress2(nextFilters.address2);
    setPage(getPageFromSearchParams(searchParams));
  }, [searchParams]);

  const params: DistanceCacheListParams = useMemo(
    () => ({
      site_type: showSiteFilter && appliedSiteTypes.length > 0 ? appliedSiteTypes : undefined,
      clinic_number: appliedClinicNumber || undefined,
      address1: appliedAddress1 || undefined,
      address2: appliedAddress2 || undefined,
      sort_by: sortBy,
      order: order === 'ascend' ? 'asc' : order === 'descend' ? 'desc' : undefined,
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [appliedSiteTypes, appliedClinicNumber, appliedAddress1, appliedAddress2, showSiteFilter, sortBy, order, page]
  );

  const { data, isLoading, isFetching } = useAdminDistanceCachesList(site ?? null, params);

  const innerData = data?.data;
  const rows = innerData?.data ?? [];
  const meta = innerData?.meta
    ? {
        current_page: innerData.meta.current_page,
        last_page: innerData.meta.last_page,
        per_page: innerData.meta.per_page,
        total: innerData.meta.total,
      }
    : { current_page: 1, last_page: 1, per_page: DEFAULT_PER_PAGE, total: 0 };

  const handleSearch = () => {
    const nextParams = new URLSearchParams();
    if (showSiteFilter) {
      siteTypes
        .map((v) => v.trim())
        .filter(Boolean)
        .forEach((v) => nextParams.append('site_types', v));
    }
    if (clinicNumber.trim()) nextParams.set('clinic_number', clinicNumber.trim());
    if (address1.trim()) nextParams.set('address1', address1.trim());
    if (address2.trim()) nextParams.set('address2', address2.trim());
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleSort = (
    sorter: SorterResult<DistanceCacheListItem> | SorterResult<DistanceCacheListItem>[]
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextParams = new URLSearchParams(searchParams);
    if (single.order) {
      nextParams.set('sort_by', (single.field || single.columnKey) as string);
      nextParams.set('order', single.order);
    } else {
      nextParams.delete('sort_by');
      nextParams.delete('order');
    }
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const [csvLoading, setCsvLoading] = useState(false);
  const handleCsv = useCallback(async () => {
    if (!site) return;
    setCsvLoading(true);
    try {
      await downloadDistanceCachesCsv(site, params);
      toast.success(CSV_SUCCESS_MESSAGE);
    } catch {
      toast.error(CSV_ERROR_MESSAGE);
    } finally {
      setCsvLoading(false);
    }
  }, [site, params, toast]);

  const handleChangePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  if (isAuthLoading) return <LoadingTemplate />;

  return (
    <AdminClinicsListTemplate
      breadcrumbItems={[{ title: '距離キャッシュ' }]}
      title="距離キャッシュ"
      filter={
        <DistanceCachesFilterPanel
          tenants={tenantSiteTypeList}
          showSiteFilter={showSiteFilter}
          siteTypes={siteTypes}
          hasAllSelected={hasAllSelected}
          onChangeSiteTypes={setSiteTypes}
          clinicNumber={clinicNumber}
          onChangeClinicNumber={setClinicNumber}
          address1={address1}
          onChangeAddress1={setAddress1}
          address2={address2}
          onChangeAddress2={setAddress2}
          onSearch={handleSearch}
          onCsv={handleCsv}
          csvLoading={csvLoading}
        />
      }
      table={
        <DistanceCachesTableSection
          rows={rows}
          loading={isLoading || isFetching}
          meta={meta}
          defaultPerPage={DEFAULT_PER_PAGE}
          canUseSiteTypeFeatures={showSiteFilter}
          tenantNameBySiteType={tenantNameBySiteType}
          sortInfo={{ columnKey: sortBy, order: order ?? undefined }}
          onSort={handleSort}
          onChangePage={handleChangePage}
        />
      }
    />
  );
};

export default SiteAdminDistanceCachesListPage;
